import { defineStore } from 'pinia'
import type { WebChatMessage, MessageCard, WorkflowRequest, ToolActivity } from '~/types/web-chat'
import type { ViewState } from '~~/shared/web-chat/viewState'
import type { ConversationDetail } from '~~/shared/chat/conversation'
import { useDocumentsStore } from '~/stores/documents'
import { fromWire, toWire } from '~~/shared/web-chat/scope'
import { normalizeWebChatToolCall, type CitationSnippetData } from '~/composables/web-chat/normalizers'
import { useChatCitations } from '~/composables/web-chat/useChatCitations'
import { useConfirmCards } from '~/composables/web-chat/useConfirmCards'
import { useChatStreaming, type StreamingState } from '~/composables/web-chat/useChatStreaming'
import { useWorkflowActions } from '~/composables/web-chat/useWorkflowActions'
import { useGroundingRecovery } from '~/composables/web-chat/useGroundingRecovery'
import { useChatScope } from '~/composables/web-chat/useChatScope'
import { useChatJurisdiction } from '~/composables/web-chat/useChatJurisdiction'
import { useChatAttachments } from '~/composables/web-chat/useChatAttachments'
import { useDocPreview } from '~/composables/web-chat/useDocPreview'
import { useWorkflowDraftSync } from '~/composables/web-chat/useWorkflowDraftSync'
import { useMessagePersistence } from '~/composables/web-chat/useMessagePersistence'

export type { WebChatMessage, MessageAttachment, MessageCard } from '~/types/web-chat'
export type { WebChatToolCall } from '~/composables/web-chat/useChatStreaming'

/**
 * The chat's state, and a FACADE over `app/composables/web-chat/` (MAR-948 phase 4).
 *
 * This file owns the refs that span concerns — the thread, the open conversation, the streaming
 * flags — and the conversation lifecycle (load, switch, new, delete). Everything else is a composable
 * that receives what it needs as deps, built here in DEPENDENCY ORDER: the scope first, because the
 * draft sync, the workflow actions and the confirm cards all read it. None of them calls
 * `useWebChatStore()` back (`webChatStoreSplitGuard.test.ts`); the return surface is pinned by
 * `webChatStoreFacade.test.ts`.
 */
export const useWebChatStore = defineStore('web-chat', () => {
  const { authFetch } = useAuthFetch()
  const { apiFetch, apiStreamFetch } = useApi()
  const { modelId } = useModelSelector()
  const { qraVersion } = useQraVersion()
  const { t } = useI18n()
  const chats = useChatsStore()

  /**
   * The router, resolved once and defensively: the hermetic suite instantiates this store with no
   * Nuxt router. Navigation is a side effect of the preview mirror and two workflow actions, not
   * something the store needs in order to exist, so a missing router degrades to "no navigation".
   */
  const routing = (() => {
    try { return { router: useRouter(), route: useRoute() } } catch { return null }
  })()
  const firebaseDb = (() => {
    try {
      const ensure = (useNuxtApp() as { $ensureFirebaseDb?: () => Promise<unknown> }).$ensureFirebaseDb
      return typeof ensure === 'function' ? ensure : null
    } catch { return null }
  })()

  // Typed as `Ref<WebChatMessage[]>` (not the default `ref()` return) so Vue's `UnwrapRef` is never
  // applied to `WebChatMessage`: its `toolCalls[].input` is the recursive `JsonValue`, which sends
  // `UnwrapRef`'s structural recursion past the compiler's instantiation-depth limit (TS2589).
  const messages = ref([]) as Ref<WebChatMessage[]>
  const isStreaming = ref(false)
  const toolStatus = ref('')
  // The chat's live line (MAR-1057): whether the streaming turn waits for its answer, and its
  // server-tool activity. Display only and never on a message, so nothing persists it; set by
  // `useChatStreaming`'s stream handlers (see its deps).
  const awaitingAnswer = ref(false)
  const toolActivities = ref<ToolActivity[]>([])
  // The typed UI contract reported each turn (gates client tools + rendered into the agent thread).
  // See shared/web-chat/viewState.
  const view = ref<ViewState>({})
  const citationSnippets = ref(new Map<string, CitationSnippetData>())
  const conversationId = ref<string | null>(null)
  const loadingConversation = ref(false)
  // True from the moment a send begins until startNewChat() clears it. Covers the optimistic window
  // before createConversation()/the first message push land, so the UI switches to the chat view
  // without flashing the welcome screen. Owned here so that any startNewChat() caller resets it.
  const chatActive = ref(false)
  let scrollRequester: (() => void) | null = null

  // Mutable streaming state shared with the streaming composable (abort/active-index/saved-id).
  const streamingState: StreamingState = { abortController: null, activeAssistantIndex: -1, assistantPersisted: false }

  const MESSAGE_WINDOW = 20

  // ── Composed concerns, in dependency order ──────────────────────────────────────────────────────
  const scope = useChatScope({
    conversationId,
    persistScope: (id, wire) => chats.setConversationScope(id, wire),
    revealContextRail: () => usePanelLayout().revealContextRail(),
  })
  const jurisdiction = useChatJurisdiction({
    conversationId,
    apiFetch,
    persistJurisdiction: (id, code) => chats.setConversationJurisdiction(id, code),
  })
  const attachments = useChatAttachments({ conversationId, apiFetch, documents: () => useDocumentsStore() })
  const preview = useDocPreview({ routing, closeLaw: () => usePanelLayout().closeLaw() })
  const draftSync = useWorkflowDraftSync({
    conversationId,
    activeCaseId: scope.activeCaseId,
    activeClientId: scope.activeClientId,
    apiFetch,
    onDocumentRegenerated: preview.bumpIfShowing,
  })

  function cancel() {
    streamingState.abortController?.abort()
  }

  const persistence = useMessagePersistence({ conversationId, messages, isStreaming, state: streamingState, apiFetch, cancel })

  const citations = useChatCitations(citationSnippets)
  const confirmCards = useConfirmCards({
    messages,
    requestScroll,
    apiStreamFetch,
    activeScope: scope.activeScope,
    attachDocument: attachments.attachDocument,
  })

  const streaming = useChatStreaming({
    messages,
    isStreaming,
    toolStatus,
    awaitingAnswer,
    toolActivities,
    view,
    contextDocumentIds: attachments.contextDocumentIds,
    conversationId,
    activeScope: scope.activeScope,
    chatActive,
    modelId,
    qraVersion,
    state: streamingState,
    messageWindow: MESSAGE_WINDOW,
    t,
    authFetch,
    requestScroll,
    createConversation,
    saveMessage: persistence.saveMessage,
    saveMessages: persistence.saveMessages,
    updateMessage: persistence.updateMessage,
    applyConfirmRequest: confirmCards.applyConfirmRequest,
    mergeCitationSnippets: citations.mergeCitationSnippets,
    resolveCitations: () => citations.resolveMessageCitations(messages.value, apiFetch),
    // A queued Slow turn is followed over Firestore (MAR-1390). Resolved here, inside the store's
    // setup, because the plugin's `$ensureFirebaseDb` hangs off the Nuxt app — and defensively, like
    // the router above: the hermetic suite builds this store with no Nuxt app at all.
    ensureFirestore: firebaseDb ?? undefined,
  })
  const { sendMessage, resumeWithClientToolResults } = streaming
  /** "Envíamelo por correo" on a queued Slow turn (MAR-1424) — see `useChatStreaming`. */
  const requestQueuedTurnEmail = (messageId: string) => streaming.requestQueuedTurnEmail(messageId)
  const { recoverGrounding } = useGroundingRecovery({ messages, isStreaming, sendMessage, apiFetch })

  const workflowActions = useWorkflowActions({
    messages,
    isStreaming,
    apiFetch,
    sendMessage,
    // Travels onto the run so the Activity Center can link back to this thread (MAR-109).
    conversationId,
    router: { push: to => routing?.router.push(to) },
    openDocPreview: preview.openDocPreview,
    // Read at settle time, not captured: a run takes minutes, and whether the user is still on this
    // conversation decides whether opening the finished document is helpful or a hijack.
    currentPath: computed(() => routing?.route.path ?? ''),
    activeCaseId: scope.activeCaseId,
    postWorkflowDraft: draftSync.postWorkflowDraft,
    flushWorkflowDraft: draftSync.flushWorkflowDraft,
    saveWorkflowProposal: draftSync.saveWorkflowProposal,
  })

  // ── Derived views ───────────────────────────────────────────────────────────────────────────────
  const conversations = computed(() => chats.conversations)
  /** The open conversation's row in the chats store — the one lookup its title/favourite read. */
  const currentConversation = computed(() =>
    conversationId.value ? chats.conversations.find(c => c.id === conversationId.value) ?? null : null)
  const conversationTitle = computed(() => currentConversation.value?.title || '')
  const conversationFavorite = computed(() => currentConversation.value?.is_favorite || false)

  // Single source of truth for "show the chat view vs. the welcome screen".
  const hasActiveChat = computed(() => !!conversationId.value || messages.value.length > 0 || chatActive.value)

  /**
   * The chat's current workflow negotiation (MAR-437 follow-up) — found by scanning the thread for
   * the most recent `run_workflow` card rather than mirrored into a separate object: the card's
   * `request.fields[].value` IS the values, so there is nothing to keep in sync and nothing to clear
   * on a switch. A declined card is excluded — the user said no, and the rail stops showing it.
   */
  const activeWorkflowRequest = computed<WorkflowRequest | null>(() => {
    for (let i = messages.value.length - 1; i >= 0; i--) {
      const card = messages.value[i]?.card
      if (card?.toolId === 'run_workflow' && card.request.status !== 'declined') return card.request
    }
    return null
  })

  function setView(v: ViewState) {
    view.value = v
  }

  function setScrollRequester(fn: (() => void) | null) {
    scrollRequester = fn
  }

  function requestScroll() {
    scrollRequester?.()
  }

  // ── Conversation lifecycle ──────────────────────────────────────────────────────────────────────
  async function listConversations() {
    return chats.listConversations()
  }

  async function createConversation(firstMessage: string): Promise<string> {
    // Only a pick the org still offers is sent; a newer pick made while this request is in flight is
    // written to the row once the id exists (MAR-1418 audit). The picker then adopts what the row
    // HOLDS, which is `null` when the org removed the sent country after the options were fetched
    // (MAR-1418 F2). Not awaited: it never rejects, and the first message must not wait on it.
    const sentJurisdiction = jurisdiction.jurisdictionForCreate.value
    const created = await chats.createConversation(firstMessage, toWire(scope.activeScope.value), sentJurisdiction)
    const id = created.id
    conversationId.value = id
    void jurisdiction.settleCreatedConversation(id, sentJurisdiction, created.jurisdiction)
    // Preserve the pending scope so switching away and back restores the chip.
    scope.rememberConversationScope(id, scope.activeScope.value)
    // AFTER the id is set: the attachments the composer gathered before this row existed (#83).
    attachments.flushAttachments()
    return id
  }

  const messageCache = new Map<string, WebChatMessage[]>()
  const MAX_CACHE = 10

  function cacheMessages(id: string, msgs: WebChatMessage[]) {
    messageCache.delete(id)
    messageCache.set(id, msgs)
    if (messageCache.size > MAX_CACHE) messageCache.delete(messageCache.keys().next().value!)
  }

  /**
   * What leaving a conversation resets, for `switchTo` and `startNewChat` alike.
   *
   * `clearAttachments` runs FIRST, and is local-only: the outgoing thread's chips are not the next
   * one's (#83), and it must drop them before `restoreConversationDocuments` resolves or the new
   * thread renders both sets. It writes nothing, so neither thread's remembered rows are touched —
   * which is also why it is not `detachDocument`, whose DELETEs would name the wrong conversation.
   */
  function resetConversationView() {
    attachments.clearAttachments()
    citationSnippets.value = new Map()
    streamingState.assistantPersisted = false
    preview.closeDocPreview()
  }

  function switchTo(id: string, msgs: WebChatMessage[]) {
    if (conversationId.value && conversationId.value !== id) cacheMessages(conversationId.value, messages.value.slice())
    resetConversationView()
    conversationId.value = id
    messages.value = msgs
    // An already-loaded chat's saved rung wins over the current selection (D3, MAR-990). A chat not
    // in the cache yet adopts nothing here; `loadConversation` adopts the row once it has it.
    scope.adoptConversationScope(id)
    jurisdiction.adoptConversationJurisdiction(id)
  }

  function restoreDocuments(id: string) {
    attachments.restoreConversationDocuments(id).catch(err =>
      console.warn('[webChatStore] attachment restore on load failed:', err))
  }

  async function loadConversation(id: string) {
    if (conversationId.value === id) return

    const cached = messageCache.get(id)
    if (cached) {
      switchTo(id, cached)
      messageCache.delete(id)
      // The messages were cached; the thread's documents never were.
      restoreDocuments(id)
      return
    }

    switchTo(id, [])
    loadingConversation.value = true
    try {
      const data = await apiFetch<ConversationDetail>(`/api/conversations/${id}`)
      if (conversationId.value !== id) return

      // The row's scope, refreshed into the cache and ADOPTED, silently (D3, MAR-990). For a
      // case-scoped chat the client is THE CASE'S client (the CHECK constraint keeps the
      // conversation's own `client_id` null whenever a case is set), so the chip reads
      // "Client › Case" without a second fetch.
      const stored = fromWire({
        caseId: data.case_id,
        caseName: data.case_name,
        clientId: data.case_id ? data.case_client_id : data.client_id,
        clientName: data.case_id ? data.case_client_name : data.client_name,
      })
      scope.rememberConversationScope(id, stored)
      scope.adoptConversationScope(id, stored)
      // The row's jurisdiction wins too (MAR-1418); the server re-checks it on every turn regardless.
      jurisdiction.adoptConversationJurisdiction(id, data.jurisdiction ?? null)

      messages.value = data.messages.map(m => ({
        id: m.id,
        role: m.role,
        content: m.content,
        toolCalls: Array.isArray(m.tool_calls) ? m.tool_calls.map(normalizeWebChatToolCall) : undefined,
        timestamp: Number(m.timestamp),
      }))

      // Four best-effort restores. Each failure leaves that part as it was rather than breaking the
      // load: citation snippets derived from the cite markers (the same path a live turn uses), the
      // confirm cards rebuilt from their persisted tool calls (MAR-991), the workflow status cards
      // this thread started (MAR-437, rebuilt from the run), and the documents this thread
      // remembers (#83). The confirm cards go FIRST: their card-building half is synchronous, and
      // the workflow restore only fills a message that has no card yet.
      citations.resolveMessageCitations(messages.value, apiFetch).catch(err =>
        console.warn('[webChatStore] citation resolve on load failed:', err))
      confirmCards.rehydrateConfirmCards(id).catch(err =>
        console.warn('[webChatStore] confirm card rehydrate on load failed:', err))
      workflowActions.rehydrateWorkflowCards(id).catch(err =>
        console.warn('[webChatStore] workflow card rehydrate on load failed:', err))
      restoreDocuments(id)
    } finally {
      loadingConversation.value = false
    }
  }

  async function renameConversation(title: string) {
    if (!conversationId.value) return
    await chats.renameConversation(conversationId.value, title)
  }

  function toggleFavorite() {
    if (currentConversation.value) chats.toggleFavorite(currentConversation.value)
  }

  async function deleteConversation(id: string) {
    await chats.deleteConversation(id)
    messageCache.delete(id)
    if (conversationId.value === id) {
      conversationId.value = null
      messages.value = []
      jurisdiction.resetJurisdiction()
    }
  }

  function startNewChat() {
    // Before `conversationId` is nulled — see `resetConversationView`. Without it the chips leaked
    // forward, and `createConversation` then flushed them into the NEW thread's documents: a durable
    // wrong answer to "what is this chat about".
    resetConversationView()
    conversationId.value = null
    messages.value = []
    chatActive.value = false
    // UNLIKE the scope below, the jurisdiction is never carried forward (MAR-1418): a new chat starts
    // on the org's main, so picking another country is always a deliberate act.
    jurisdiction.resetJurisdiction()
    // KEEP whatever the user currently has selected: a new chat continues where they were working.
    // This used to reset to global and re-seed from storage, which silently dropped a client with no
    // cases (absent from the case-built allow-list) out of the context rail. `applyRememberedScope`
    // no-ops unless the scope is global, so it still seeds a genuinely unscoped new chat.
    scope.applyRememberedScope()
  }

  /**
   * Begin a fresh chat filed under a case. Callers pass the client when they have it — a case rung
   * built without one renders as a bare case name where the same rung loaded from its row renders
   * `Client › Case`, and the half-built rung is what `rememberScope` wrote to storage.
   */
  function startCaseChat(caseItem: { id: string; name: string; clientId?: string | null; clientName?: string | null }) {
    startNewChat()
    void scope.setActiveCase(caseItem)
  }

  /**
   * Settle a confirm client tool the agent paused on, by resuming the session with the outcome.
   *
   * Takes the CARD: it carries its own `callId`, so there is no scan for "the newest message pending
   * this tool", and clearing `callId` makes a second call a no-op — a double-click cannot resume
   * twice. `toolName` differs from `toolId` only for the generated per-workflow tools.
   */
  async function resolveConfirmToolCall(card: MessageCard, response: unknown): Promise<void> {
    const callId = card.callId
    // A rehydrated card carries no paused tool call — legitimate, and worth saying, since it is
    // otherwise indistinguishable from a resume that silently failed.
    if (!callId) {
      console.info(`[webChat] ${card.toolId}: settled with no paused tool call to resume (a rehydrated card, or one already resumed).`)
      return
    }
    // The message the card sits on is where the continuation streams. Live, that is the turn still
    // being tracked; for a card rebuilt after a reload (MAR-991) nothing is tracked any more, and
    // without the anchor the resume would silently do nothing after the effect had already run.
    const anchorIndex = messages.value.findIndex(m => m.card?.callId === callId)
    card.callId = undefined
    await resumeWithClientToolResults(
      [{ id: callId, name: card.toolName ?? card.toolId, response }],
      anchorIndex >= 0 ? { anchorIndex } : undefined,
    )
  }

  return {
    messages,
    isStreaming,
    toolStatus,
    awaitingAnswer,
    toolActivities,
    contextDocumentIds: attachments.contextDocumentIds,
    contextDocuments: attachments.contextDocuments,
    attachmentsProcessing: attachments.attachmentsProcessing,
    attachmentsProcessingCount: attachments.attachmentsProcessingCount,
    isAttached: attachments.isAttached,
    attachDocument: attachments.attachDocument,
    detachDocument: attachments.detachDocument,
    citationSnippets,
    citationsResolving: citations.citationsResolving,
    conversationId,
    conversationTitle,
    conversationFavorite,
    conversations,
    loadingConversation,
    activeScope: scope.activeScope,
    activeCaseId: scope.activeCaseId,
    activeCaseName: scope.activeCaseName,
    setScope: scope.setScope,
    setKnownScopeIds: scope.setKnownScopeIds,
    applyRememberedScope: scope.applyRememberedScope,
    clearScopeForDeleted: scope.clearScopeForDeleted,
    setActiveCase: scope.setActiveCase,
    jurisdictionOptions: jurisdiction.jurisdictionOptions,
    activeJurisdiction: jurisdiction.activeJurisdiction,
    jurisdictionError: jurisdiction.jurisdictionError,
    ensureJurisdictionOptions: jurisdiction.ensureJurisdictionOptions,
    setJurisdiction: jurisdiction.setJurisdiction,
    startCaseChat,
    setView,
    setScrollRequester,
    sendMessage,
    resumeWithClientToolResults,
    requestQueuedTurnEmail,
    resolveConfirmToolCall,
    acceptWorkflow: workflowActions.acceptWorkflow,
    acceptConversion: workflowActions.acceptConversion,
    declineWorkflow: workflowActions.declineWorkflow,
    openWorkflowForm: workflowActions.openWorkflowForm,
    recoverGrounding,
    pendingCard: confirmCards.pendingCard,
    settleCard: confirmCards.settleCard,
    downloadDocument: confirmCards.downloadDocument,
    previewDocument: preview.previewDocument,
    openDocPreview: preview.openDocPreview,
    closeDocPreview: preview.closeDocPreview,
    activeWorkflowRequest,
    syncWorkflowField: draftSync.syncWorkflowField,
    queueWorkflowDraftSync: draftSync.queueWorkflowDraftSync,
    flushWorkflowDraft: draftSync.flushWorkflowDraft,
    cancel,
    startNewChat,
    hasActiveChat,
    listConversations,
    createConversation,
    loadConversation,
    renameConversation,
    toggleFavorite,
    deleteConversation,
    truncateFrom: persistence.truncateFrom,
  }
})
